"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SearchableInput = SearchableInput;
exports.isInteractiveElement = isInteractiveElement;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importDefault(require("react"));
const react_2 = require("@floating-ui/react");
const react_native_1 = require("react-native");
const react_native_fontawesome_1 = require("@fortawesome/react-native-fontawesome");
const react_i18next_1 = require("react-i18next");
const ThemeContext_1 = require("../ThemeContext");
const Theme_1 = require("../Theme");
const ResponsiveContext_1 = require("../contexts/ResponsiveContext");
const SearchableInput_shared_1 = require("./SearchableInput.shared");
const ModalWrapper_1 = require("./ModalWrapper");
const OverlayContext_1 = require("../contexts/OverlayContext");
const toSizeUnit = (value) => typeof value === 'number' ? `${value}px` : value;
function resolveMaxLimit(containerMaxLimit, availableLimit) {
    if (containerMaxLimit == null || containerMaxLimit === "")
        return availableLimit;
    if (typeof containerMaxLimit === "number") {
        return Math.min(availableLimit, containerMaxLimit);
    }
    const s = String(containerMaxLimit).trim();
    if (s.endsWith("%")) {
        const pct = parseFloat(s);
        if (!isNaN(pct))
            return Math.min(availableLimit, (pct / 100) * availableLimit);
    }
    const m = s.match(/^([\d.]+)(px|vh|vw)?$/i);
    if (m) {
        const val = parseFloat(m[1]);
        const unit = (m[2] || "px").toLowerCase();
        if (unit === "px")
            return Math.min(availableLimit, val);
        if (unit === "vh")
            return Math.min(availableLimit, (val / 100) * window.innerHeight);
        if (unit === "vw")
            return Math.min(availableLimit, (val / 100) * window.innerWidth);
    }
    const v = parseFloat(s);
    if (!isNaN(v))
        return Math.min(availableLimit, v);
    return availableLimit;
}
function useFloatingSearchState({ floatingOptions = {
    strategy: 'fixed',
    placement: 'bottom-end',
    maxHeight: 350,
}, floatingEnabled = true, // for different screen sizes, we can disable floating and use a different layout
 }) {
    const { size: responsiveSize } = (0, ResponsiveContext_1.useResponsiveContext)();
    const { isOpen, setIsOpen, activeIndex, setActiveIndex, setReason, listRef, capsuleRef, leftButtonRef, rightButtonRef, clearButtonRef, triggerButtonRef, disabled, openOnClick, searchable, } = (0, SearchableInput_shared_1.useSearchable)();
    const { refs, floatingStyles, context } = (0, react_2.useFloating)({
        placement: floatingEnabled ? (floatingOptions?.placement || 'bottom-end') : undefined,
        strategy: floatingOptions?.strategy || 'fixed',
        open: isOpen,
        onOpenChange: (open, event, reason) => {
            setActiveIndex(null);
            setIsOpen(open);
            setReason(reason);
        },
        whileElementsMounted: floatingEnabled
            ? (reference, floating, update) => (0, react_2.autoUpdate)(reference, floating, update, {
                ancestorScroll: true,
                ancestorResize: true,
                elementResize: true,
                layoutShift: true,
            })
            : undefined,
        middleware: floatingEnabled
            ? [
                (0, react_2.offset)(4),
                (0, react_2.flip)({ fallbackPlacements: ['bottom-start', 'top-end'] }),
                (0, react_2.size)({
                    apply({ elements, availableHeight, rects }) {
                        const maxH = resolveMaxLimit(floatingOptions?.maxHeight, availableHeight);
                        const nextWidth = toSizeUnit(Math.max(rects.reference.width, floatingOptions?.minWidth || 0));
                        requestAnimationFrame(() => {
                            if (elements.floating) {
                                elements.floating.style.setProperty('--floating-max-height', toSizeUnit(maxH));
                                elements.floating.style.setProperty('--floating-width', nextWidth);
                            }
                        });
                    },
                }),
            ]
            : [],
    });
    react_1.default.useEffect(() => {
        if (!isOpen)
            return;
        const handlePopState = () => {
            setIsOpen(false);
            setReason(null);
        };
        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [isOpen]);
    const role = (0, react_2.useRole)(context, { enabled: floatingEnabled, role: 'combobox' });
    const dismiss = (0, react_2.useDismiss)(context, {
        enabled: floatingEnabled && !disabled,
        outsidePressEvent: 'pointerdown',
        ancestorScroll: responsiveSize !== 'sm',
        bubbles: true,
        outsidePress: (event) => {
            const target = event.target;
            if (!target)
                return true;
            const domRef = context.refs.domReference.current;
            const floatingEl = context.refs.floating.current;
            const capsuleInstance = capsuleRef.current;
            const capsuleDomNode = capsuleInstance?._inputRef ||
                capsuleInstance?.root ||
                (capsuleInstance instanceof HTMLElement ? capsuleInstance : null);
            const leftEl = leftButtonRef.current;
            const rightEl = rightButtonRef.current;
            const clearEl = clearButtonRef.current;
            const triggerEl = triggerButtonRef.current;
            if (domRef?.contains(target) ||
                floatingEl?.contains(target) ||
                capsuleDomNode?.contains?.(target) ||
                leftEl?.contains(target) ||
                rightEl?.contains(target) ||
                clearEl?.contains(target) ||
                triggerEl?.contains(target)) {
                return false;
            }
            return true;
        }
    });
    const listNavigation = (0, react_2.useListNavigation)(context, {
        enabled: floatingEnabled && !disabled,
        listRef,
        activeIndex,
        virtual: floatingEnabled && searchable ? true : false,
        loop: true,
        focusItemOnOpen: false,
        onNavigate: (index) => {
            setActiveIndex(index);
        }
    });
    const click = (0, react_2.useClick)(context, {
        enabled: floatingEnabled && openOnClick && !disabled,
        toggle: !searchable,
    });
    const useReferenceBlurInteraction = (context) => {
        const referenceProps = react_1.default.useMemo(() => ({
            onBlur: (event) => {
                if (!floatingEnabled)
                    return;
                if (disabled)
                    return;
                const relatedTarget = event.nativeEvent?.relatedTarget || document.activeElement;
                const domRef = context.refs.domReference.current;
                const floatingEl = context.refs.floating.current;
                const isInsideSearch = (domRef && domRef.contains(relatedTarget)) ||
                    (floatingEl && floatingEl.contains(relatedTarget));
                if (isOpen) {
                    if (isInsideSearch) {
                        return;
                    }
                    // To not lose focus when the focus goes to another item without having pressed outside
                    if (!relatedTarget || relatedTarget === document.body) {
                        requestAnimationFrame(() => {
                            domRef?.focus();
                        });
                        return;
                    }
                }
                else {
                    setReason('blur-closed');
                }
            },
        }), [isOpen, context.refs, floatingEnabled, disabled]);
        return react_1.default.useMemo(() => ({
            reference: referenceProps,
        }), [referenceProps]);
    };
    const { getReferenceProps, getFloatingProps, getItemProps } = (0, react_2.useInteractions)([role, click, dismiss, listNavigation, useReferenceBlurInteraction(context)]);
    return react_1.default.useMemo(() => ({
        floatingEnabled,
        refs,
        floatingStyles,
        context,
        getReferenceProps,
        getFloatingProps,
        getItemProps
    }), [
        floatingEnabled,
        refs,
        floatingStyles,
        context,
        getReferenceProps,
        getFloatingProps,
        getItemProps
    ]);
}
function FloatingSearchProvider({ children, floatingOptions, floatingEnabled, }) {
    const floating = useFloatingSearchState({
        floatingOptions,
        floatingEnabled,
    });
    return ((0, jsx_runtime_1.jsx)(SearchableInput_shared_1.FloatingSearchContext.Provider, { value: floating, children: children }));
}
function SearchableInput({ children, ...options }) {
    const searchable = (0, SearchableInput_shared_1.useSearchableCore)(options);
    return ((0, jsx_runtime_1.jsx)(SearchableInput_shared_1.SearchableContext.Provider, { value: searchable, children: (0, jsx_runtime_1.jsx)(FloatingSearchProvider, { floatingOptions: options.floatingOptions, floatingEnabled: options.floatingEnabled, children: children }) }));
}
SearchableInput.Header = function SearchableHeader({ children }) {
    const floating = (0, SearchableInput_shared_1.useOptionalFloatingSearch)();
    const { isOpen, activeIndex, listRef, setIsOpen, setActiveIndex, setSearchText, setReason, closeWhenTextCleared } = (0, SearchableInput_shared_1.useSearchable)();
    if (!react_1.default.isValidElement(children))
        return null;
    const handleTextChange = (input) => {
        const nextValue = typeof input === 'string' ? input : (input?.target?.value ?? '');
        setSearchText(nextValue);
        if (!isOpen && nextValue.length > 0) {
            setIsOpen(true);
            setReason('user-initiated');
        }
        else if (isOpen && nextValue.length === 0 && closeWhenTextCleared) {
            setIsOpen(false);
            setReason('user-initiated');
        }
    };
    const baseProps = {
        "data-state": isOpen ? "open" : "closed",
        "spellCheck": false,
        "autoCorrect": "off",
        "autoComplete": "off",
        "autoCapitalize": "none",
        onChange: handleTextChange,
        onKeyDown(event) {
            // Prevent default behavior for IME composition to avoid triggering actions while composing characters
            if (event.nativeEvent.isComposing) {
                return;
            }
            if (event.key === 'Enter' && isOpen) {
                if (activeIndex === null || activeIndex === undefined) {
                    return;
                }
                // Avoid default behavior and stop propagation to prevent form submission or other side effects
                event.preventDefault();
                event.stopPropagation();
                const targetNode = listRef.current[activeIndex];
                if (targetNode) {
                    if (typeof targetNode.click === 'function') {
                        targetNode.click();
                    }
                }
                return;
            }
            if (event.key === 'Tab' && isOpen) {
                setIsOpen(false);
                setReason('focus-out');
                setActiveIndex(null);
                return;
            }
        }
    };
    const finalProps = (floating?.floatingEnabled && floating?.getReferenceProps)
        ? floating.getReferenceProps({
            ref: floating.context.refs.setReference,
            positionRef: floating.context.refs.setPositionReference,
            ...baseProps
        })
        : baseProps;
    return react_1.default.cloneElement(children, finalProps);
};
const TYPEABLE_SELECTOR = "input:not([type='hidden']):not([disabled])," +
    "[contenteditable]:not([contenteditable='false']),textarea:not([disabled])";
function isInteractiveElement(element) {
    return (element?.closest(`button,a[href],[role="button"],select,[tabindex]:not([tabindex="-1"]),${TYPEABLE_SELECTOR}`) != null);
}
SearchableInput.Group = function SearchableGroup({ children, asChild, style, onMouseDown, ref, ...props }) {
    const floating = (0, SearchableInput_shared_1.useOptionalFloatingSearch)();
    const { isOpen, setIsOpen, setReason, openOnClick, disabled } = (0, SearchableInput_shared_1.useSearchable)();
    const groupRef = (0, react_2.useMergeRefs)([ref, floating?.context?.refs?.setPositionReference]);
    const handleMouseDown = (event) => {
        onMouseDown?.(event);
        if (event.defaultPrevented) {
            return;
        }
        const target = event.target;
        const domRef = floating?.context?.refs?.domReference?.current;
        if (target !== event.currentTarget && isInteractiveElement(target)) {
            return;
        }
        event.preventDefault();
        if (disabled) {
            return;
        }
        if (domRef) {
            domRef.focus();
            if (!isOpen && openOnClick) {
                setIsOpen(true);
                setReason(null);
            }
        }
    };
    const combinedStyle = {
        flex: 1,
        ...style,
    };
    if (asChild && react_1.default.isValidElement(children)) {
        const child = children;
        const childRef = child.ref || child.props?.ref;
        const mergedAsChildRef = (0, react_2.useMergeRefs)([groupRef, childRef]);
        return react_1.default.cloneElement(child, {
            role: child.props.role ?? "group",
            ...props,
            ...child.props,
            ref: mergedAsChildRef,
            style: {
                ...combinedStyle,
                ...child.props.style,
            },
            onMouseDown: (e) => {
                handleMouseDown(e);
                child.props.onMouseDown?.(e);
            },
        });
    }
    return ((0, jsx_runtime_1.jsx)("div", { ref: groupRef, role: "group", onMouseDown: handleMouseDown, style: combinedStyle, ...props, children: children }));
};
SearchableInput.Content = function SearchableContent({ children, }) {
    const { layerHostDomNode } = (0, OverlayContext_1.useOverlay)();
    const modalRootNode = react_1.default.useContext(ModalWrapper_1.ModalDomContext);
    const targetPortalRoot = modalRootNode ?? layerHostDomNode ?? undefined;
    const { isOpen, backgroundColor, searchable } = (0, SearchableInput_shared_1.useSearchable)();
    const floatingContext = (0, SearchableInput_shared_1.useOptionalFloatingSearch)();
    const THEME = (0, ThemeContext_1.useTheme)();
    if (!isOpen || !floatingContext)
        return null;
    const { refs, floatingStyles, context, getFloatingProps, floatingEnabled } = floatingContext;
    const desktopStyle = {
        boxSizing: 'border-box',
        borderRadius: 4,
        padding: 4,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 100000,
        backgroundColor: backgroundColor,
        boxShadow: `0px 0px 4px ${THEME.Primary}33`,
        borderWidth: 1,
        borderColor: `${THEME.Primary}33`,
        borderStyle: 'solid',
        overflow: 'hidden',
        maxHeight: 'var(--floating-max-height, 350px)',
        width: 'var(--floating-width, auto)',
        ...floatingStyles,
    };
    const mobileModalStyle = {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 100000,
        backgroundColor: backgroundColor || THEME.CardBackground,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
    };
    const appliedContainerStyle = floatingEnabled ? desktopStyle : mobileModalStyle;
    const floatingProps = getFloatingProps({
        ref: refs.setFloating,
        style: appliedContainerStyle,
    });
    return ((0, jsx_runtime_1.jsxs)(react_2.FloatingPortal, { root: targetPortalRoot, children: [!floatingEnabled && ((0, jsx_runtime_1.jsx)("div", { onClick: () => context.onOpenChange(false), style: {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: '#00000044',
                    zIndex: 99999,
                } })), (0, jsx_runtime_1.jsx)(react_2.FloatingFocusManager, { context: context, modal: !floatingEnabled, initialFocus: floatingEnabled ? -1 : 0, order: ["reference", "content"], returnFocus: true, children: (0, jsx_runtime_1.jsx)("div", { ...floatingProps, children: children }) })] }));
};
SearchableInput.Item = function SearchableItem({ index, item, children, style, onPress, isHeader = false, isCollapsed, ...props }) {
    const THEME = (0, ThemeContext_1.useTheme)();
    const isDark = (0, ThemeContext_1.useIsDark)();
    const floating = (0, SearchableInput_shared_1.useOptionalFloatingSearch)();
    const { collapsibleSections, valueKey, listRef, activeIndex, selectItem, selectedValue, toggleSection, searchFields, activeColor, } = (0, SearchableInput_shared_1.useSearchable)();
    const isHeaderItem = isHeader || item?.isHeader;
    const isCollapsedItem = isCollapsed || item?.isCollapsed;
    const itemIndex = index ?? item?.index;
    const isFocused = activeIndex === itemIndex;
    const itemValue = (0, SearchableInput_shared_1.resolveFieldValue)(item, valueKey);
    const itemRawValue = isHeaderItem ? null : itemValue;
    const isItemSelected = isHeaderItem ? false : selectedValue.includes(String(itemRawValue));
    const resolvedUserStyle = typeof style === 'function' ? style({ hovered: false }) : style;
    const activeBackgroundColor = (() => {
        const focusedHeaderColor = activeColor == "primary" ? THEME.PrimaryLight : THEME.SecondaryLight;
        const focusedItemColor = THEME.PrimaryBackgroundLightHover;
        const selectedItemColor = activeColor == "primary" ? THEME.PrimaryBackgroundLightSelected : THEME.PrimaryBackgroundSecondaryLightSelected;
        if (isFocused) {
            if (isHeaderItem)
                return focusedHeaderColor;
            return focusedItemColor;
        }
        if (isItemSelected) {
            return selectedItemColor;
        }
        return undefined;
    })();
    const defaultStyle = isHeaderItem
        ? {
            gap: 8,
            flexDirection: 'row',
            alignItems: 'center',
            backgroundColor: isDark ? THEME.SmokeWhite : `${THEME.Gray}55`,
            borderTopRightRadius: 4,
            borderTopLeftRadius: 4,
            height: 32,
            paddingHorizontal: 12,
            ...(isCollapsedItem ? { borderBottomLeftRadius: 4, borderBottomRightRadius: 4 } : {}),
            ...{
                backdropFilter: 'blur(14px)',
                WebkitBackdropFilter: 'blur(14px)',
            },
        }
        :
            {
                paddingVertical: searchFields.length > 1 ? 8 : 12,
                paddingHorizontal: 12,
                cursor: 'pointer',
            };
    const activeStyle = activeBackgroundColor ? { backgroundColor: activeBackgroundColor } : {};
    const handlePress = (e) => {
        onPress?.(e);
        if (isHeaderItem) {
            if (item?.groupKey) {
                toggleSection(item.groupKey);
            }
        }
        else {
            selectItem(item);
        }
    };
    const baseProps = {
        role: isHeaderItem ? 'button' : 'option',
        'aria-posinset': isHeaderItem ? undefined : item?.posInset,
        'aria-setsize': isHeaderItem ? undefined : item?.setSize,
        'aria-expanded': isHeaderItem ? !(isCollapsedItem) : undefined,
        'aria-selected': isHeaderItem ? undefined : isItemSelected,
        //@ts-ignore
        onPress: handlePress,
        style: {
            ...defaultStyle,
            ...resolvedUserStyle,
            ...activeStyle,
        },
        ...props,
    };
    const finalProps = (floating?.floatingEnabled && floating?.getItemProps
        ? floating.getItemProps({
            id: isHeaderItem ? `search-header-${itemIndex}` : `search-item-${itemIndex}`,
            ref: (node) => {
                if (node) {
                    listRef.current[itemIndex] = node.nativeEvent?.target || node;
                }
                else {
                    listRef.current[itemIndex] = null;
                }
            },
            tabIndex: -1,
            onMouseDown(e) {
                e.preventDefault();
            },
            ...baseProps,
        })
        : baseProps);
    return ((0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: (0, jsx_runtime_1.jsx)(react_native_1.TouchableOpacity, { ...finalProps, children: (0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [collapsibleSections && isHeaderItem && ((0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: isCollapsedItem ? Theme_1.ICONS.Regular.ChevronDown : Theme_1.ICONS.Regular.ChevronUp, size: 12, color: THEME.Text })), children] }) }) }));
};
SearchableInput.Empty = function SearchableEmpty({ children, style, ...props }) {
    return ((0, jsx_runtime_1.jsx)(react_native_1.View, { role: "status", "aria-live": "polite", "aria-atomic": "true", style: style, ...props, children: children }));
};
SearchableInput.Clear = function SearchableClear({ onClear, style, disabled = false, ...props }) {
    const { searchText, setSearchText, clearButtonRef, } = (0, SearchableInput_shared_1.useSearchable)();
    const THEME = (0, ThemeContext_1.useTheme)();
    const { t } = (0, react_i18next_1.useTranslation)();
    const { size } = (0, ResponsiveContext_1.useResponsiveContext)();
    const [isHovered, setIsHovered] = react_1.default.useState(false);
    const ICON_SIZE = size === 'sm' ? 28 : 24;
    const visible = searchText !== '';
    const handleClear = () => {
        if (disabled || !visible)
            return;
        setSearchText('');
        onClear?.();
    };
    return ((0, jsx_runtime_1.jsx)(react_native_1.Pressable
    // @ts-ignore
    , { 
        // @ts-ignore
        ref: clearButtonRef, disabled: disabled, role: "button", tabIndex: -1, "aria-hidden": true, onPointerDown: (e) => {
            if (disabled || !visible)
                return;
            e.preventDefault();
            e.stopPropagation();
        }, onPress: handleClear, onHoverIn: () => setIsHovered(true), onHoverOut: () => setIsHovered(false), style: ({ pressed }) => ({
            width: ICON_SIZE,
            height: ICON_SIZE,
            borderRadius: ICON_SIZE / 2,
            alignItems: 'center',
            justifyContent: 'center',
            opacity: visible ? 1 : 0,
            pointerEvents: visible ? 'auto' : 'none',
            backgroundColor: disabled
                ? 'transparent'
                : pressed
                    ? `${THEME.Primary}33`
                    : isHovered
                        ? `${THEME.Primary}11`
                        : 'transparent',
            ...(typeof style === 'function' ? style({ pressed }) : style),
        }), ...props, children: (0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { "aria-hidden": true, icon: Theme_1.ICONS.Regular.Xmark, size: 16, color: THEME.OnSurfaceVariant }) }));
};
SearchableInput.Trigger = function SearchableTrigger({ children, disabled = false, style, error = false, ...props }) {
    const floating = (0, SearchableInput_shared_1.useOptionalFloatingSearch)();
    const { isOpen, setIsOpen, setReason, triggerButtonRef } = (0, SearchableInput_shared_1.useSearchable)();
    const THEME = (0, ThemeContext_1.useTheme)();
    const { size } = (0, ResponsiveContext_1.useResponsiveContext)();
    const { t } = (0, react_i18next_1.useTranslation)();
    const ICON_SIZE = size === 'sm' ? 28 : 24;
    const handleToggle = () => {
        if (disabled)
            return;
        setIsOpen(!isOpen);
        if (!isOpen && floating?.context) {
            const domRef = floating.context.refs.domReference.current;
            domRef?.focus();
        }
    };
    const [isHovered, setIsHovered] = react_1.default.useState(false);
    return ((0, jsx_runtime_1.jsx)(react_native_1.Pressable, { ref: triggerButtonRef, disabled: disabled, role: "button", tabIndex: -1, "aria-label": isOpen ? t('common:hints.close') : t('common:hints.open'), "aria-expanded": isOpen, onPointerDown: (e) => {
            if (disabled)
                return;
            e.preventDefault();
            e.stopPropagation();
        }, 
        //@ts-ignore
        onKeyDown: (event) => {
            if (disabled)
                return;
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                setIsOpen(true);
                setReason('list-navigation');
                if (floating?.context) {
                    const domRef = floating.context.refs.domReference.current;
                    domRef?.focus();
                }
            }
        }, onPress: handleToggle, onHoverIn: () => setIsHovered(true), onHoverOut: () => setIsHovered(false), style: ({ pressed }) => ({
            width: ICON_SIZE,
            height: ICON_SIZE,
            borderRadius: ICON_SIZE / 2,
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: disabled ? 'none' : 'auto',
            backgroundColor: disabled
                ? 'transparent'
                : pressed
                    ? `${THEME.Primary}33`
                    : isHovered
                        ? `${THEME.Primary}11`
                        : 'transparent',
            ...(typeof style === 'function' ? style({ pressed }) : style),
        }), ...props, children: children ?? ((0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { "aria-hidden": true, icon: isOpen ? Theme_1.ICONS.Regular.ChevronUp : Theme_1.ICONS.Regular.ChevronDown, color: disabled ? THEME.OnSurfaceDisabled : error ? THEME.Red : THEME.OnSurfaceVariant, size: 14 })) }));
};
